import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

export interface DropdownItem {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  separator?: boolean;
}

export interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: "start" | "end" | "center";
  className?: string;
}

export function DropdownMenu({
  trigger,
  items,
  align = "start",
  className = "",
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectableItems = items.filter((it) => !it.separator && !it.disabled);

  useEffect(() => {
    if (!open) {
      setFocusedIdx(-1);
      return;
    }
    function onMouse(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && focusedIdx >= 0) {
      itemRefs.current[focusedIdx]?.focus();
    }
  }, [open, focusedIdx]);

  const nonSepItems = items.map((it, i) => ({ ...it, originalIdx: i })).filter((it) => !it.separator && !it.disabled);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIdx((prev) => (prev + 1) % nonSepItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIdx((prev) => (prev - 1 + nonSepItems.length) % nonSepItems.length);
    }
  }

  let selectableCount = -1;

  return (
    <div
      ref={rootRef}
      className={`dropdown-root ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        className="dropdown-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </div>

      {open && (
        <div
          className={`dropdown-content dropdown-${align}`}
          role="menu"
        >
          {items.map((item, i) => {
            if (item.separator) {
              return <div key={i} className="dropdown-separator" role="separator" />;
            }
            selectableCount++;
            const idx = selectableCount;
            return (
              <button
                key={i}
                ref={(el) => { itemRefs.current[idx] = el; }}
                className="dropdown-item"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                tabIndex={open ? 0 : -1}
              >
                {item.icon && <span className="dropdown-item-icon">{item.icon}</span>}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
